import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Search,
  ChevronLeft,
  Users as UsersIcon,
  Plus,
  Trash2,
  CheckSquare,
  Square,
  Save,
} from "lucide-react";
import { financeAPI, educationAPI } from "../api/axiosClient";
import { toast } from "react-hot-toast";
import { SimpleDropdown } from "../components/ui/simple-dropdown";
import MultiSelectDropdown from "../components/ui/multi-select-dropdown";
import { NumericFormat } from "react-number-format";

interface CreateReceivableViewProps {
  onBack: () => void;
  onSuccess?: () => void;
}

const CreateReceivableView: React.FC<CreateReceivableViewProps> = ({
  onBack,
  onSuccess,
}) => {
  const [createLoading, setCreateLoading] = useState<boolean>(false);
  const [loadingStudents, setLoadingStudents] = useState<boolean>(false);
  const [classrooms, setClassrooms] = useState<any[]>([]);
  const [loadingClassrooms, setLoadingClassrooms] = useState<boolean>(false);
  const [selectedClassroomIds, setSelectedClassroomIds] = useState<string[]>(
    [],
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedSearchStudents, setSelectedSearchStudents] = useState<
    string[]
  >([]);
  const [assignedStudents, setAssignedStudents] = useState<any[]>([]);
  const [selectedAssignedStudents, setSelectedAssignedStudents] = useState<
    string[]
  >([]);
  const [bulkPrice, setBulkPrice] = useState<number>(0);
  const [newReceivable, setNewReceivable] = useState({
    type: 0,
    paymentType: 0,
    currency: 0,
    period: "",
    name: "",
    description: "",
    lastPaymentDate: "",
  });

  // Currency options (numeric values for API)
  const currencyOptions = [
    { id: "0", value: 0, label: "₺ Türk Lirası (TRY)" },
  ];

  // Period (month) options
  const periodOptions = [
    { id: "2026-01", value: "2026-01", label: "Ocak 2026" },
    { id: "2026-02", value: "2026-02", label: "Şubat 2026" },
    { id: "2026-03", value: "2026-03", label: "Mart 2026" },
    { id: "2026-04", value: "2026-04", label: "Nisan 2026" },
    { id: "2026-05", value: "2026-05", label: "Mayıs 2026" },
    { id: "2026-06", value: "2026-06", label: "Haziran 2026" },
    { id: "2026-07", value: "2026-07", label: "Temmuz 2026" },
    { id: "2026-08", value: "2026-08", label: "Ağustos 2026" },
    { id: "2026-09", value: "2026-09", label: "Eylül 2026" },
    { id: "2026-10", value: "2026-10", label: "Ekim 2026" },
    { id: "2026-11", value: "2026-11", label: "Kasım 2026" },
    { id: "2026-12", value: "2026-12", label: "Aralık 2026" },
  ];

  // Ödeme tipleri (numeric values for API)
  const paymentTypes = [
    { id: "0", value: 0, label: "Kayıt Ücreti", isShow: true },
    { id: "1", value: 1, label: "Servis Ücreti", isShow: true },
    { id: "2", value: 2, label: "Yemek Ücreti", isShow: true },
    { id: "3", value: 3, label: "Etkinlik Ücreti", isShow: false },
    { id: "4", value: 4, label: "Kitap Ücreti", isShow: true },
  ];

  // Fetch classrooms on mount
  useEffect(() => {
    fetchClassrooms();
  }, []);

  useEffect(() => {
    console.log(newReceivable);
  }, [newReceivable]);

  // Fetch classrooms
  const fetchClassrooms = async () => {
    setLoadingClassrooms(true);
    try {
      const response = await educationAPI.getClassrooms({
        Q: "",
        Page: 1,
        Size: 100,
        OrderBy: 0,
        SortBy: "Name",
      });
      if (response.success && response.data?.items) {
        setClassrooms(
          response.data.items.map((classroom: any) => ({
            id: classroom.id,
            value: classroom.id,
            label: classroom.name,
            name: classroom.name,
            grade: classroom.grade,
            section: classroom.section,
          })),
        );
      }
    } catch (error) {
      console.error("Error fetching classrooms:", error);
    } finally {
      setLoadingClassrooms(false);
    }
  };

  // Fetch students for search (filtered by classrooms)
  const fetchStudentsForSearch = async () => {
    if (selectedClassroomIds.length === 0) {
      setSearchResults([]);
      return;
    }

    setLoadingStudents(true);
    try {
      const selectedClassroomsList = classrooms.filter((c) =>
        selectedClassroomIds.includes(c.id),
      );

      const params: any = {
        Q: searchTerm,
        Page: 1,
        Size: 100,
        OrderBy: 0,
        SortBy: "StudentNumber",
      };

      if (selectedClassroomsList.length > 0) {
        params.GradeSectionFilters = selectedClassroomsList.map(
          (classroom) => ({
            grade: classroom.grade,
            section: classroom.section,
          }),
        );
      }

      const response = await educationAPI.getStudents(params);
      if (response.success && response.data?.items) {
        const assignedIds = assignedStudents.map((s) => s.studentId || s.id);
        const filteredStudents = response.data.items.filter(
          (student: any) =>
            !assignedIds.includes(student.studentId || student.id),
        );
        setSearchResults(filteredStudents);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
    } finally {
      setLoadingStudents(false);
    }
  };

  // Select/Deselect search student
  const handleSelectSearchStudent = (studentId: string) => {
    setSelectedSearchStudents((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId],
    );
  };

  // Select all search students
  const handleSelectAllSearchStudents = () => {
    if (selectedSearchStudents.length === searchResults.length) {
      setSelectedSearchStudents([]);
    } else {
      setSelectedSearchStudents(searchResults.map((s) => s.studentId || s.id));
    }
  };

  // Add selected students to assigned list
  const handleAddSelectedStudents = () => {
    const studentsToAdd = searchResults
      .filter((s) => selectedSearchStudents.includes(s.studentId || s.id))
      .map((s) => ({
        ...s,
        amount: 0,
      }));

    setAssignedStudents((prev) => [...prev, ...studentsToAdd]);
    setSelectedSearchStudents([]);
    setSearchResults((prev) =>
      prev.filter((s) => !selectedSearchStudents.includes(s.studentId || s.id)),
    );
  };

  // Select/Deselect assigned student
  const handleSelectAssignedStudent = (studentId: string) => {
    setSelectedAssignedStudents((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId],
    );
  };

  // Select all assigned students
  const handleSelectAllAssignedStudents = () => {
    if (selectedAssignedStudents.length === assignedStudents.length) {
      setSelectedAssignedStudents([]);
    } else {
      setSelectedAssignedStudents(
        assignedStudents.map((s) => s.studentId || s.id),
      );
    }
  };

  // Remove selected students from assigned list
  const handleRemoveSelectedStudents = () => {
    setAssignedStudents((prev) =>
      prev.filter(
        (s) => !selectedAssignedStudents.includes(s.studentId || s.id),
      ),
    );
    setSelectedAssignedStudents([]);
  };

  // Update student amount
  const handleUpdateStudentAmount = (studentId: string, amount: number) => {
    setAssignedStudents((prev) =>
      prev.map((s) =>
        (s.studentId || s.id) === studentId ? { ...s, amount } : s,
      ),
    );
  };

  // Apply bulk price to all assigned students
  const handleApplyBulkPrice = () => {
    if (bulkPrice <= 0) {
      toast.error("Lütfen geçerli bir tutar girin");
      return;
    }
    setAssignedStudents((prev) =>
      prev.map((s) => ({ ...s, amount: bulkPrice })),
    );
    toast.success(
      `Tüm öğrencilere ${bulkPrice.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} tutarı uygulandı`,
    );
  };

  // Calculate total amount
  const totalAmount = assignedStudents.reduce(
    (sum, student) => sum + (student.amount || 0),
    0,
  );

  const formatBackendMessage = (msg?: string, students: any[] = []) => {
    if (!msg) return msg;
    const idRegex =
      /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi;

    const ids = msg.match(idRegex);
    if (!ids) return msg;

    const map = new Map<string, string>();
    students.forEach((s) => {
      const id = (s.studentId || s.id)?.toString()?.toLowerCase();
      if (!id) return;
      const fullName = `${s.firstName ?? ""} ${s.lastName ?? ""}`.trim();
      map.set(id, fullName || id);
    });
    return msg.replace(idRegex, (id) => map.get(id.toLowerCase()) ?? id);
  };

  const handleCreateReceivable = async () => {
    // Validation
    if (newReceivable.paymentType < 0) {
      toast.error("Lütfen bir ödeme tipi seçin");
      return;
    }
    if (!newReceivable.name.trim()) {
      toast.error("Lütfen başlık girin");
      return;
    }
    if (!newReceivable.period) {
      toast.error("Lütfen dönem seçin");
      return;
    }
    if (!newReceivable.lastPaymentDate) {
      toast.error("Lütfen son ödeme tarihi girin");
      return;
    }
    if (assignedStudents.length === 0) {
      toast.error("Lütfen en az bir öğrenci seçin");
      return;
    }

    const studentsWithoutAmount = assignedStudents.filter(
      (s) => !s.amount || s.amount <= 0,
    );
    if (studentsWithoutAmount.length > 0) {
      toast.error("Lütfen tüm öğrenciler için geçerli bir tutar girin");
      return;
    }

    setCreateLoading(true);
    try {
      const studentsPayload = assignedStudents.map((student) => ({
        studentId: student.studentId || student.id,
        price: student.amount,
      }));

      const periodDate = new Date(
        `${newReceivable.period}-01T00:00:00.000Z`,
      ).toISOString();

      const lastPaymentDateISO = new Date(
        `${newReceivable.lastPaymentDate}T23:59:59.000Z`,
      ).toISOString();

      const response = await financeAPI.createReceivable({
        name: newReceivable.name,
        period: periodDate,
        type: newReceivable.type,
        paymentType: newReceivable.paymentType,
        currency: newReceivable.currency,
        lastPaymentDate: lastPaymentDateISO,
        description: newReceivable.description,
        students: studentsPayload,
      });

      if (response?.success) {
        toast.success(`${assignedStudents.length} öğrenci için ödeme talebi başarıyla oluşturuldu`);
        onSuccess?.();
        onBack();
      } else {
        const msg = formatBackendMessage(response?.message, assignedStudents);
        toast.error(msg || "Ödeme talebi oluşturulurken hata oluştu");
      }

      } catch (error: any) {
        console.error("Error creating receivable:", error);

        const rawMsg =
          error?.response?.data?.message ||
          error?.response?.data?.Message ||
          error?.message;

        const msg = formatBackendMessage(rawMsg, assignedStudents);
        toast.error(msg || "Ödeme talebi oluşturulurken hata oluştu");
      }  finally {
      setCreateLoading(false);
    }
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="sm"
            onClick={onBack}
            className="flex items-center"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Geri
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Yeni Ödeme Talebi
            </h1>
            <p className="text-sm text-gray-500">
              Öğrenci seçin ve ücret belirleyin
            </p>
          </div>
        </div>
        <Button
          onClick={handleCreateReceivable}
          className="bg-[#9B51E0] hover:bg-[#8B41D0] text-white"
          disabled={createLoading || assignedStudents.length === 0}
        >
          {createLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Kaydediliyor...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Kaydet ({assignedStudents.length} Öğrenci)
            </>
          )}
        </Button>
      </div>

      {/* Content */}
      <div
        className="grid grid-cols-3 gap-6"
        style={{ minHeight: "calc(100vh - 220px)" }}
      >
        {/* Sol Panel - Temel Bilgiler */}
        <div className="flex flex-col space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                Ödeme Talebi Bilgileri
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Title */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">
                  Başlık *
                </Label>
                <Input
                  value={newReceivable.name}
                  onChange={(e) =>
                    setNewReceivable((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  placeholder="Ödeme talebi başlığı"
                />
              </div>

              {/* Period Selection */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">
                  Dönem *
                </Label>
                <SimpleDropdown
                  options={periodOptions}
                  value={
                    periodOptions.find((p) => p.id === newReceivable.period) ||
                    null
                  }
                  onChange={(option) => {
                    const period = option?.value ? String(option.value) : "";
                    setNewReceivable((prev) => ({ ...prev, period }));
                  }}
                  placeholder="Dönem seçin..."
                />
              </div>

              {/* Payment Type Selection */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">
                  Ödeme Tipi *
                </Label>
                <SimpleDropdown
                  options={paymentTypes.filter((pt) => pt.isShow)}
                  value={
                    paymentTypes.find(
                      (pt) => pt.value === newReceivable.paymentType,
                    ) || null
                  }
                  onChange={(option) => {
                    const paymentType =
                      option?.value !== undefined ? Number(option.value) : -1;
                    setNewReceivable((prev) => ({
                      ...prev,
                      paymentType,
                    }));
                  }}
                  placeholder="Ödeme tipi seçin..."
                />
              </div>

              {/* Currency Selection */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">
                  Para Birimi *
                </Label>
                <SimpleDropdown
                  options={currencyOptions}
                  value={
                    currencyOptions.find(
                      (c) => c.value === newReceivable.currency,
                    ) || null
                  }
                  onChange={(option) => {
                    const currency =
                      option?.value !== undefined ? Number(option.value) : 0;
                    setNewReceivable((prev) => ({
                      ...prev,
                      currency,
                    }));
                  }}
                  placeholder="Para birimi seçin..."
                />
              </div>

              {/* Son Ödeme Tarihi */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">
                  Son Ödeme Tarihi *
                </Label>
                <Input
                  type="date"
                  value={newReceivable.lastPaymentDate}
                  onChange={(e) =>
                    setNewReceivable((prev) => ({
                      ...prev,
                      lastPaymentDate: e.target.value,
                    }))
                  }
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">
                  Açıklama
                </Label>
                <textarea
                  value={newReceivable.description}
                  onChange={(e) =>
                    setNewReceivable((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Ödeme talebi açıklaması"
                  rows={4}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#9B51E0] focus:border-transparent resize-none"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Orta Panel - Öğrenci Ara ve Filtrele */}
        <div className="flex flex-col">
          <Card className="flex-1 flex flex-col">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center">
                <Search className="w-4 h-4 mr-2 text-blue-600" />
                Öğrenci Ara ve Filtrele
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col space-y-4">
              {/* Search Input */}
              <div className="flex space-x-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Ad, soyad veya numara ile ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Button
                  onClick={fetchStudentsForSearch}
                  disabled={selectedClassroomIds.length === 0}
                  className="bg-[#9B51E0] hover:bg-[#8B41D0] text-white"
                >
                  Filtrele
                </Button>
              </div>

              {/* Classroom Filter - Multiple Select */}
              <div className="space-y-2">
                <MultiSelectDropdown
                  options={classrooms}
                  selectedValues={selectedClassroomIds}
                  onChange={(values: string[]) => {
                    setSelectedClassroomIds(values);
                    setSearchResults([]);
                  }}
                  placeholder={
                    loadingClassrooms
                      ? "Yükleniyor..."
                      : "Sınıf/Şube seçin (birden fazla seçilebilir)"
                  }
                  loading={loadingClassrooms}
                  maxHeight="200px"
                />
              </div>

              {/* Add Selected Button */}
              <Button
                onClick={handleAddSelectedStudents}
                disabled={selectedSearchStudents.length === 0}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Seçili Kayıtları Ekle ({selectedSearchStudents.length})
              </Button>

              {/* Search Results Table */}
              {loadingStudents ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B51E0]"></div>
                </div>
              ) : searchResults.length === 0 ? (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <UsersIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">Sınıf seçin ve filtreleyin</p>
                  </div>
                </div>
              ) : (
                <div className="flex-1 overflow-hidden border border-gray-200 rounded-lg">
                  <div className="overflow-y-auto h-full">
                    <table className="w-full">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="w-10 px-2 py-3 text-center">
                            <button
                              onClick={handleSelectAllSearchStudents}
                              className="flex items-center justify-center text-sm font-medium text-gray-700 hover:text-gray-900"
                              title="Tümünü Seç"
                            >
                              {selectedSearchStudents.length ===
                              searchResults.length ? (
                                <CheckSquare className="w-4 h-4" />
                              ) : (
                                <Square className="w-4 h-4" />
                              )}
                            </button>
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-700">
                            Öğrenci
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-700">
                            Sınıf
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {searchResults.map((student) => (
                          <tr
                            key={student.studentId || student.id}
                            className={`hover:bg-gray-50 cursor-pointer ${selectedSearchStudents.includes(student.studentId || student.id) ? "bg-blue-50" : ""}`}
                            onClick={() =>
                              handleSelectSearchStudent(
                                student.studentId || student.id,
                              )
                            }
                          >
                            <td className="w-10 px-2 py-3 text-center">
                              {selectedSearchStudents.includes(
                                student.studentId || student.id,
                              ) ? (
                                <CheckSquare className="w-4 h-4 text-blue-600" />
                              ) : (
                                <Square className="w-4 h-4 text-gray-400" />
                              )}
                            </td>
                            <td className="px-3 py-3">
                              <div>
                                <span className="text-sm font-medium text-gray-900">
                                  {student.firstName} {student.lastName}
                                </span>
                                <p className="text-xs text-gray-500">
                                  {student.studentNumber || "-"}
                                </p>
                              </div>
                            </td>
                            <td className="px-3 py-3 text-sm text-gray-600">
                              {student.classroomName ||
                                student.className ||
                                "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sağ Panel - Seçilen Öğrenciler ve Ücretler */}
        <div className="flex flex-col">
          <Card className="flex-1 flex flex-col">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center">
                  <UsersIcon className="w-4 h-4 mr-2 text-green-600" />
                  Seçilen Öğrenciler ({assignedStudents.length})
                </CardTitle>
                {assignedStudents.length > 0 && (
                  <Button
                    onClick={handleRemoveSelectedStudents}
                    disabled={selectedAssignedStudents.length === 0}
                    variant="outline"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    size="sm"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Seçili Sil ({selectedAssignedStudents.length})
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col space-y-4">
              {/* Toplu Fiyat Uygulama */}
              {assignedStudents.length > 0 && (
                <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <Label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                    Toplu Fiyat:
                  </Label>
                  <NumericFormat
                    value={bulkPrice || ""}
                    customInput={Input}
                    thousandSeparator="."
                    decimalSeparator=","
                    decimalScale={2}
                    fixedDecimalScale
                    allowNegative={false}
                    onValueChange={(values: any) => {
                      setBulkPrice(values?.floatValue ?? 0);
                    }}
                    placeholder="0,00"
                    className="w-32 h-8 text-sm"
                  />
                  <Button
                    onClick={handleApplyBulkPrice}
                    disabled={!bulkPrice || bulkPrice <= 0}
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap"
                  >
                    Tümüne Uygula
                  </Button>
                </div>
              )}

              {assignedStudents.length === 0 ? (
                <div className="flex-1 flex items-center justify-center text-gray-500 border border-gray-200 rounded-lg">
                  <div className="text-center">
                    <UsersIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>Henüz öğrenci seçilmedi</p>
                    <p className="text-sm mt-2">
                      Ortadaki panelden öğrenci seçin ve ekleyin
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex-1 overflow-hidden border border-gray-200 rounded-lg">
                  <div className="overflow-y-auto h-full">
                    <table className="w-full">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="w-10 px-2 py-3 text-center">
                            <button
                              onClick={handleSelectAllAssignedStudents}
                              className="flex items-center justify-center text-sm font-medium text-gray-700 hover:text-gray-900"
                              title="Tümünü Seç"
                            >
                              {selectedAssignedStudents.length ===
                              assignedStudents.length ? (
                                <CheckSquare className="w-4 h-4" />
                              ) : (
                                <Square className="w-4 h-4" />
                              )}
                            </button>
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-700">
                            Öğrenci
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-700">
                            Sınıf
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-700">
                            Tutar (
                            {newReceivable.currency === 0
                              ? "₺"
                              : newReceivable.currency === 1
                                ? "₺"
                                : "₺"}
                            )
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {assignedStudents.map((student) => (
                          <tr
                            key={student.studentId || student.id}
                            className={`hover:bg-gray-50 ${selectedAssignedStudents.includes(student.studentId || student.id) ? "bg-red-50" : ""}`}
                          >
                            <td className="w-10 px-2 py-3 text-center">
                              <button
                                onClick={() =>
                                  handleSelectAssignedStudent(
                                    student.studentId || student.id,
                                  )
                                }
                                className="flex items-center justify-center"
                              >
                                {selectedAssignedStudents.includes(
                                  student.studentId || student.id,
                                ) ? (
                                  <CheckSquare className="w-4 h-4 text-red-600" />
                                ) : (
                                  <Square className="w-4 h-4 text-gray-400" />
                                )}
                              </button>
                            </td>
                            <td className="px-3 py-3">
                              <div>
                                <span className="font-medium text-gray-900 text-sm">
                                  {student.firstName} {student.lastName}
                                </span>
                                <p className="text-xs text-gray-500">
                                  {student.studentNumber || "-"}
                                </p>
                              </div>
                            </td>
                            <td className="px-3 py-3 text-sm text-gray-600">
                              {student.classroomName ||
                                student.className ||
                                "-"}
                            </td>
                            <td className="px-3 py-3">
                              <NumericFormat
                                value={student.amount || ""}
                                customInput={Input}
                                thousandSeparator="."
                                decimalSeparator=","
                                decimalScale={2}
                                fixedDecimalScale
                                allowNegative={false}
                                onValueChange={(values: any) => {
                                  handleUpdateStudentAmount(
                                    student.studentId || student.id,
                                    values?.floatValue ?? 0,
                                  );
                                }}
                                placeholder="0,00"
                                className="w-28 h-8 text-sm"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Toplam Tutar */}
              {assignedStudents.length > 0 && (
                <div className="mt-auto pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-700">
                        Toplam Tutar:
                      </span>
                      <span className="text-xs text-gray-500">
                        ({assignedStudents.length} öğrenci)
                      </span>
                    </div>
                    <span className="text-lg font-bold text-green-700">
                      {totalAmount.toLocaleString("tr-TR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                      {newReceivable.currency === 0
                        ? " ₺"
                        : newReceivable.currency === 1
                          ? " ₺"
                          : " ₺"}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CreateReceivableView;
